/**
 * Keeps a simplified mesh's triangles inside one texture chart each (R5: the dark patches on far tree canopies).
 *
 * The kit's models are smart-UV-projected: every model is dozens of UV charts packed into one atlas, and the vertices
 * along a chart border are split (same position, a different UV on each side). Simplifying without crossing those
 * seams stalls at 45-85 % of the triangles, so lod.ts simplifies with `Permissive`, which collapses across them. A
 * triangle can then take one corner from chart A and another from chart B: its UVs span the atlas between the two
 * charts and it draws whatever lies there, mostly the dark gutter between charts. On a canopy that was 16-26 % of
 * the surface drawn as dark smears.
 *
 * `seamSafe` re-homes every such triangle into one chart: a corner that has a twin in the chosen chart (the vertex
 * on the other side of the split) switches to it; a corner that has none gets a new vertex (its own attributes) with
 * the UV of the chart's vertex nearest to it, so the triangle samples only texels of that chart. Extending the chart's
 * linear map to the corner instead was tried: the kit's charts are small and ragged, and at the haze LOD it still
 * drew 14 of p_tree_oak's 672 triangles and 7 of p_tree_pine_l's 492 on the gutter; the nearest vertex leaves 4 and 1
 * (Permissive alone: 52 and 53). Pure (typed arrays in and out), so tools/art/unit_lod.mjs uses the same code for the unit LODs.
 */

/** A seam-safe reduction: `keep[i]` is the source vertex output vertex i copies; `uv` every output UV, when any was borrowed. */
export interface SeamSafe { readonly keep: Uint32Array; readonly index: Uint32Array; readonly uv: Float32Array | null }

/** Positions closer than this (in model units, after quantization) are the same point; the kit's grid is ~1e-5. */
const WeldEpsilon = 1e-5;

const keyOf = (p: ArrayLike<number>, i: number): string =>
  `${Math.round(p[i * 3] / WeldEpsilon)},${Math.round(p[i * 3 + 1] / WeldEpsilon)},${Math.round(p[i * 3 + 2] / WeldEpsilon)}`;

/**
 * Chart id per vertex: vertices joined by a triangle, or at the same point with the same UV (a split for the normal
 * only, a hard edge inside one chart), are one chart.
 */
export function chartsOf(index: ArrayLike<number>, pos: ArrayLike<number>, uv: ArrayLike<number>): Int32Array {
  const n = pos.length / 3;
  const parent = new Int32Array(n);
  for (let i = 0; i < n; i++) parent[i] = i;
  const find = (i: number): number => { while (parent[i] !== i) { parent[i] = parent[parent[i]]; i = parent[i]; } return i; };
  const join = (a: number, b: number): void => { a = find(a); b = find(b); if (a !== b) parent[a] = b; };
  for (let t = 0; t < index.length; t += 3) { join(index[t], index[t + 1]); join(index[t], index[t + 2]); }
  const same = new Map<string, number>();
  for (let i = 0; i < n; i++) {
    const k = `${keyOf(pos, i)}|${uv[i * 2]},${uv[i * 2 + 1]}`;
    const j = same.get(k);
    if (j === undefined) same.set(k, i); else join(i, j);
  }
  const out = new Int32Array(n);
  for (let i = 0; i < n; i++) out[i] = find(i);
  return out;
}

/**
 * Re-homes the triangles of `reduced` (an index over the source vertices, from the simplifier) that mix charts.
 * `source` is the full-detail index the charts are found from.
 */
export function seamSafe(source: ArrayLike<number>, reduced: ArrayLike<number>, pos: ArrayLike<number>, uv: ArrayLike<number>): SeamSafe {
  const n = pos.length / 3;
  const chart = chartsOf(source, pos, uv);
  const members = new Map<number, number[]>();               // chart -> its vertices, for the nearest-vertex search
  for (let i = 0; i < n; i++) (members.get(chart[i]) ?? members.set(chart[i], []).get(chart[i])!).push(i);
  const twin = new Map<string, number>();                    // point + chart -> a vertex there
  for (let i = 0; i < n; i++) { const k = `${keyOf(pos, i)}#${chart[i]}`; if (!twin.has(k)) twin.set(k, i); }

  const keep: number[] = [];
  const uvOut: number[] = [];
  const remap = new Int32Array(n).fill(-1);
  const made = new Map<string, number>();                    // corner|chart -> output vertex with a borrowed UV
  let borrowed = false;
  const out = (v: number): number => {
    if (remap[v] < 0) { remap[v] = keep.length; keep.push(v); uvOut.push(uv[v * 2], uv[v * 2 + 1]); }
    return remap[v];
  };
  const index = new Uint32Array(reduced.length);
  for (let t = 0; t < reduced.length; t += 3) {
    const a = reduced[t], b = reduced[t + 1], c = reduced[t + 2];
    const ca = chart[a], cb = chart[b], cc = chart[c];
    if (ca === cb && cb === cc) { index[t] = out(a); index[t + 1] = out(b); index[t + 2] = out(c); continue; }
    const target = cb === cc ? cb : ca;                      // the majority chart, else the first corner's
    const corners = [a, b, c];
    for (let q = 0; q < 3; q++) {
      const v = corners[q];
      if (chart[v] === target) { index[t + q] = out(v); continue; }
      const same = twin.get(`${keyOf(pos, v)}#${target}`);
      if (same !== undefined) { index[t + q] = out(same); continue; }
      const key = `${v}|${target}`;
      let o = made.get(key);
      if (o === undefined) {
        o = keep.length;
        keep.push(v);
        const [u, w] = nearestUv(members.get(target)!, v, pos, uv);
        uvOut.push(u, w);
        made.set(key, o);
        borrowed = true;
      }
      index[t + q] = o;
    }
  }
  return { keep: Uint32Array.from(keep), index, uv: borrowed ? Float32Array.from(uvOut) : null };
}

/** The UV of the vertex of `list` (one chart's vertices) nearest to vertex `v`. */
function nearestUv(list: readonly number[], v: number, pos: ArrayLike<number>, uv: ArrayLike<number>): [number, number] {
  let best = list[0], bestD = Infinity;
  for (const i of list) {
    const dx = pos[i * 3] - pos[v * 3], dy = pos[i * 3 + 1] - pos[v * 3 + 1], dz = pos[i * 3 + 2] - pos[v * 3 + 2];
    const d = dx * dx + dy * dy + dz * dz;
    if (d < bestD) { bestD = d; best = i; }
  }
  return [uv[best * 2], uv[best * 2 + 1]];
}
